import React, { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMood } from '../../contexts/MoodContext';
import { useAuth } from '../../contexts/AuthContext';
import MoodCheckIn from './MoodCheckIn';
import AmbientBackground from './visual/AmbientBackground';
import FloatingParticleLayer from './visual/FloatingParticleLayer';
import { usePointerParallax } from './visual/hooks';
import { MoodId, getMood } from './moods';
import { dayKey } from './moodDates';

/**
 * MoodCheckInGate — the first-sign-in-of-the-day mood prompt.
 *
 * Mounted once (inside the Router, beside the chat widget), it shows the mood
 * screen as a full-screen overlay only when a check-in is due — i.e. the user is
 * authenticated, prompts are enabled, they have not opted to skip on open, and
 * they have not already checked in or skipped this session — AND they have not
 * already seen (or dismissed) today's prompt. That day guard is a local flag
 * keyed by email, so the prompt appears the first time they sign in each calendar
 * day and not again until the next day (even across new sessions on this device).
 *
 * The overlay offers a corner × close button and a "Skip for now" link; either
 * dismisses it for the day without saving. Saving is honest: the check-in is only
 * dismissed-as-saved when the backend actually persisted it (submitCheckIn
 * resolves true). If a save fails, the overlay stays open with an error and the
 * choice to retry or continue without saving — a failed save is never shown as
 * success.
 */
const AUTH_ROUTES = ['/login', '/complete-profile'];

// Day-scoped "already prompted" flag — modelled on MoodMate's own key convention
// (localStorage persists across sessions, unlike the per-session check-in gate).
const DAY_PROMPT_PREFIX = 'creyente:moodmate:dayprompt:v1:';
const todayStamp = () => dayKey(new Date());
const promptedToday = (email: string): boolean => {
  try { return localStorage.getItem(DAY_PROMPT_PREFIX + email) === todayStamp(); } catch { return false; }
};
const markPromptedToday = (email: string): void => {
  try { localStorage.setItem(DAY_PROMPT_PREFIX + email, todayStamp()); } catch { /* storage blocked — the session gate still prevents a re-show this session */ }
};

export const MoodCheckInGate: React.FC = () => {
  const { needsCheckIn, submitCheckIn, skipCheckIn, status } = useMood();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [preview, setPreview] = useState<MoodId | null>(null);
  const [dismissedToday, setDismissedToday] = useState(false);
  const lastMood = useRef<MoodId | null>(null);
  const parallaxRef = usePointerParallax<HTMLDivElement>();

  const email = user?.email || '';
  const onAuthRoute = AUTH_ROUTES.some((p) => location.pathname.startsWith(p));
  // Needs an email to scope the day flag; without one, fall back to the plain
  // session gate rather than writing a flag under an empty key.
  if (!needsCheckIn || onAuthRoute) return null;
  if (email && (dismissedToday || promptedToday(email))) return null;

  const previewMood = getMood(preview);
  const overlayStyle = previewMood
    ? ({ ['--mood-accent' as string]: previewMood.accent, ['--mood-soft' as string]: previewMood.soft } as React.CSSProperties)
    : undefined;

  const attempt = async (mood: MoodId) => {
    lastMood.current = mood;
    setFailed(false);
    setBusy(true);
    const ok = await submitCheckIn({ mood });
    setBusy(false);
    if (ok) {
      if (email) markPromptedToday(email);
      navigate('/moodmate');
    } else setFailed(true);
  };

  // Dismiss for the day without saving — the × close button and "Skip for now"
  // both land here. The chosen mood (if any) still personalises the session.
  const dismiss = () => {
    if (email) markPromptedToday(email);
    setDismissedToday(true);
    skipCheckIn();
  };

  return (
    <div ref={parallaxRef} className="mm-overlay" role="dialog" aria-modal="true" aria-label="Daily mood check-in" style={overlayStyle}>
      <AmbientBackground />
      <FloatingParticleLayer count={14} />
      <div className="mm-overlay__panel mm-glass">
        <button type="button" className="mm-overlay__close" aria-label="Close mood check-in" onClick={dismiss} disabled={busy}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
        <MoodCheckIn onContinue={attempt} onSkip={dismiss} onSelect={setPreview} busy={busy} />

        {failed && (
          <div className="mm-overlay__error" role="alert">
            <p className="mm-overlay__error-text">
              {status.error || 'Your mood couldn’t be saved. Check your connection and try again.'}
            </p>
            <div className="mm-overlay__error-actions">
              <button
                type="button"
                className="mm-btn mm-btn--primary mm-btn--sm"
                onClick={() => lastMood.current && attempt(lastMood.current)}
                disabled={busy}
              >
                Try again
              </button>
              <button type="button" className="mm-btn mm-btn--ghost mm-btn--sm" onClick={dismiss} disabled={busy}>
                Continue without saving
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MoodCheckInGate;
